import React, { useState, useEffect, useCallback } from 'react';
import client from './api/client';
import PageHeader from './components/PageHeader';
import CharacterSelector from './components/CharacterSelector';
import CollapsiblePanel from './components/CollapsiblePanel';
import LPRunControlPanel from './components/LPRunControlPanel';
import LPRunHistoryTable from './components/LPRunHistoryTable';
import LPRunDetailsModal from './components/LPRunDetailsModal';
import './App.css';
import './LPTrackingPage.css';

function LPTrackingPage() {
  const [lpData, setLpData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [authenticated, setAuthenticated] = useState(true);

  // LP Tracking Run state
  const [activeRun, setActiveRun] = useState(null);
  const [selectedRunId, setSelectedRunId] = useState(null);
  const [runRefreshTrigger, setRunRefreshTrigger] = useState(0);
  const [showRunHistory, setShowRunHistory] = useState(false);

  // Fetch LP data from backend
  const fetchLPData = useCallback(async (forceRefresh = false) => {
    try {
      // By default, only fetch from database (no ESI calls)
      // forceRefresh=true will fetch fresh data from EVE ESI API
      const url = forceRefresh
        ? '/lp/loyalty-points/?force_refresh=true'
        : '/lp/loyalty-points/';

      console.log(forceRefresh ? 'Fetching fresh LP data from ESI...' : 'Loading cached LP data from database...');

      const response = await client.get(url);

      if (response.data.success) {
        setLpData(response.data);
        setError(null);
        setAuthenticated(true);
      } else {
        setError(response.data.error || 'Failed to fetch loyalty points');
      }
    } catch (err) {
      console.error('Error fetching LP data:', err);
      if (err.response && err.response.status === 401) {
        setAuthenticated(false);
        setError('Please log in to view loyalty points');
      } else {
        setError('Failed to fetch loyalty points. Please try again.');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  const fetchActiveRun = useCallback(async () => {
    try {
      const response = await client.get('/lp/tracking/active-run/');
      if (response.data.success) {
        setActiveRun(response.data.active_run);
      }
    } catch (err) {
      console.error('Error fetching active run:', err);
    }
  }, []);

  const handleRunUpdated = () => {
    fetchActiveRun();
    setRunRefreshTrigger(prev => prev + 1);
  };

  useEffect(() => {
    fetchLPData();
    fetchActiveRun();
  }, [fetchLPData, fetchActiveRun]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchLPData(true);
  };

  const formatNumber = (num) => {
    return num.toLocaleString();
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const calculateTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 60) {
      return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    }

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) {
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    }

    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  };

  if (loading) {
    return (
      <div className="page">
        <PageHeader title="Loyalty Points" />
        <div className="page-status">Loading loyalty points...</div>
      </div>
    );
  }

  if (!authenticated) {
    return (
      <div className="page">
        <PageHeader title="Loyalty Points" />
        <div className="page-status">Log in with your EVE Online character to view loyalty points.</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page">
        <PageHeader title="Loyalty Points" />
        <div className="page-status error">
          {error} <button onClick={() => fetchLPData()} className="btn btn-sm">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <PageHeader title="Loyalty Points">
        <button onClick={handleRefresh} disabled={refreshing} className="btn btn-primary">
          {refreshing ? 'Refreshing...' : 'Refresh LP'}
        </button>
      </PageHeader>
      <div className="page-content">
        {lpData && lpData.characters && lpData.characters.length > 0 ? (
          <>
            <CharacterSelector trackingType="lpTracking" onCharactersUpdated={fetchActiveRun} />
            <LPRunControlPanel activeRun={activeRun} onRunUpdated={handleRunUpdated} />

            <CollapsiblePanel
              title="Run history"
              collapsed={!showRunHistory}
              onToggle={() => setShowRunHistory(!showRunHistory)}
            >
              <LPRunHistoryTable onViewDetails={setSelectedRunId} refreshTrigger={runRefreshTrigger} />
            </CollapsiblePanel>

            <section className="panel">
              <div className="panel-head">
                <h2>LP balances</h2>
                <span className="panel-sub">
                  {formatNumber(lpData.characters.reduce((sum, char) => sum + char.total_lp, 0))} LP across{' '}
                  {lpData.total_characters} character{lpData.total_characters !== 1 ? 's' : ''}
                  {lpData.refreshed_count > 0 && ` · ${lpData.refreshed_count} refreshed from ESI`}
                </span>
              </div>
              {lpData.characters.every(char => !char.loyalty_points || char.loyalty_points.length === 0) ? (
                <div className="table-empty">No LP data cached yet. Use "Refresh LP" to fetch balances from EVE Online.</div>
              ) : (
                <div className="table-scroll">
                  <table className="data-table lp-table">
                    <thead>
                      <tr>
                        <th>Corporation</th>
                        <th className="num">LP</th>
                        <th className="num">Updated</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lpData.characters.map((character) => (
                        <React.Fragment key={character.character_id}>
                          <tr className="lp-char-row">
                            <td>
                              <span className="lp-char">
                                <img
                                  src={character.portrait_64x64 || character.portrait_128x128}
                                  alt=""
                                  className="char-chip-portrait"
                                />
                                <b>{character.character_name}</b>
                                <span className="text-faint">{character.corporation_name}</span>
                                {character.is_primary && <span className="pill pill-accent">Primary</span>}
                              </span>
                            </td>
                            <td className="num"><b>{formatNumber(character.total_lp)}</b></td>
                            <td></td>
                          </tr>
                          {character.loyalty_points && character.loyalty_points.length > 0 ? (
                            character.loyalty_points.map((lp) => (
                              <tr key={`${character.character_id}-${lp.corporation_id}`}>
                                <td className="lp-corp">
                                  {lp.corporation_name}
                                  {lp.corporation_ticker && <span className="text-faint"> [{lp.corporation_ticker}]</span>}
                                </td>
                                <td className="num">{formatNumber(lp.loyalty_points)}</td>
                                <td className="num text-faint" title={formatDate(lp.fetched_at)}>
                                  {lp.cached && 'cached · '}
                                  {calculateTimeAgo(lp.fetched_at)}
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td className="lp-corp text-faint" colSpan="3">No loyalty points</td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </>
        ) : (
          <div className="page-status">No characters found. Add a character in Account Settings.</div>
        )}

        {/* Run Details Modal */}
        {selectedRunId && (
          <LPRunDetailsModal
            runId={selectedRunId}
            onClose={() => setSelectedRunId(null)}
          />
        )}
      </div>
    </div>
  );
}

export default LPTrackingPage;
